/**
 * Real-time Agent Workspace - Modern Split-Screen UI
 * 
 * Implements the 2025 standard for multi-agent interfaces:
 * - Left panel: Chat/control interface
 * - Right panel: Live agent workspace with real-time output
 * - WebSocket connection for real-time updates
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RealtimeAgentWorkspace.css';

const RealtimeAgentWorkspace = () => {
  // WebSocket and session state
  const [isConnected, setIsConnected] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);

  // Agent state
  const [agents, setAgents] = useState({
    'initial-writer': { status: 'idle', progress: 0, currentStep: '', output: '' },
    'beginner-critic': { status: 'idle', progress: 0, currentStep: '', output: '' },
    'expert-critic': { status: 'idle', progress: 0, currentStep: '', output: '' },
    'final-writer': { status: 'idle', progress: 0, currentStep: '', output: '' }
  });

  // UI state
  const [concept, setConcept] = useState('');
  const [apiKey, setApiKey] = useState(localStorage.getItem('anthropic_api_key') || '');
  const [isWorkflowRunning, setIsWorkflowRunning] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [activeTab, setActiveTab] = useState('initial-writer');
  
  // Real-time output streaming
  const [liveOutput, setLiveOutput] = useState('');
  const outputRef = useRef(null);
  const messageCounter = useRef(0);

  // Initialize WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const newSessionId = sessionId || `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${Math.random().toString(36).substr(2, 5)}`;
    if (!sessionId) setSessionId(newSessionId);

    const wsUrl = `wss://blog-writer-agents.jhenson-smallbizai.workers.dev/realtime/connect?sessionId=${newSessionId}`;
    // For local development, use: ws://localhost:8787/realtime/connect
    
    setConnectionStatus('connecting');
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      addChatMessage('system', '🟢 Connected to real-time agent workspace');
    };

    wsRef.current.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    wsRef.current.onclose = () => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
      // Don't add disconnect message to reduce chat spam
      
      // Don't auto-reconnect if backend is not available
      // setTimeout(connectWebSocket, 3000);
    };

    wsRef.current.onerror = (error) => {
      // Don't log WebSocket errors to console to reduce noise
      setConnectionStatus('error');
      addChatMessage('system', '⚠️ Backend not available - running in demo mode');
    };
  }, [sessionId]);

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'session_connected':
        addChatMessage('system', `📡 Session connected: ${message.sessionId}`);
        break;
        
      case 'agent_progress':
        const { agentId, agentType, status, progress, currentStep } = message.data;
        setAgents(prev => ({
          ...prev,
          [agentType]: {
            ...prev[agentType],
            status,
            progress,
            currentStep
          }
        }));
        
        addChatMessage('agent', `🤖 ${agentType}: ${currentStep} (${progress}%)`);
        break;
        
      case 'agent_output':
        const outputData = message.data;
        setLiveOutput(prev => prev + outputData.output);
        
        // Auto-scroll to bottom
        setTimeout(() => {
          if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
          }
        }, 100);
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  // Add message to chat
  const addChatMessage = (type, content) => {
    messageCounter.current += 1;
    setChatMessages(prev => [...prev, {
      id: `msg_${Date.now()}_${messageCounter.current}_${Math.random().toString(36).substr(2, 5)}`,
      type,
      content,
      timestamp: new Date().toLocaleTimeString()
    }]);
  };

  // Start multi-agent workflow
  const startWorkflow = async () => {
    if (!concept.trim()) {
      alert('Please enter a blog concept first!');
      return;
    }

    if (!apiKey.trim()) {
      alert('Please enter your Anthropic API key first!');
      return;
    }

    setIsWorkflowRunning(true);
    setLiveOutput('');
    addChatMessage('user', `🚀 Starting blog creation: "${concept}"`);

    try {
      const response = await fetch(`https://blog-writer-agents.jhenson-smallbizai.workers.dev/realtime/workflow/start`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Anthropic-API-Key': apiKey.trim()
        },
        body: JSON.stringify({
          concept,
          sessionId,
          userId: 'demo-user',
          apiKey: apiKey.trim()
        })
      });

      const result = await response.json();
      if (result.success) {
        addChatMessage('system', '✅ Workflow started successfully!');
      } else {
        addChatMessage('system', `❌ Error: ${result.error}`);
        setIsWorkflowRunning(false);
      }
    } catch (error) {
      // Don't log connection refused errors to console
      if (error.message.includes('Failed to fetch') || error.message.includes('ERR_CONNECTION_REFUSED')) {
        console.log('Backend not available, running in demo mode');
      } else {
        console.error('Workflow error:', error);
      }
      // If backend is not running, simulate the workflow
      addChatMessage('system', '🎭 Running in demo mode - simulating multi-agent workflow...');
      
      // Simulate agent progress with realistic timing
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'initial-writer': { status: 'working', progress: 25, currentStep: 'Analyzing concept...', output: '' }
        }));
        addChatMessage('agent', '🤖 Initial Writer: Starting blog creation...');
        setLiveOutput('Starting blog creation for: "' + concept + '"\n\n');
      }, 1000);
      
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'initial-writer': { status: 'working', progress: 50, currentStep: 'Generating outline...', output: '' }
        }));
        addChatMessage('agent', '📝 Initial Writer: Creating blog outline...');
        setLiveOutput(prev => prev + '## Blog Outline\n1. Introduction\n2. Main Content\n3. Conclusion\n\n');
      }, 3000);
      
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'initial-writer': { status: 'working', progress: 75, currentStep: 'Writing content...', output: '' }
        }));
        addChatMessage('agent', '✍️ Initial Writer: Writing blog content...');
        setLiveOutput(prev => prev + '## Blog Draft\nWriting the main content based on your concept...\n\n');
      }, 5000);
      
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'initial-writer': { status: 'completed', progress: 100, currentStep: 'Draft completed', output: '' }
        }));
        addChatMessage('agent', '✅ Initial Writer: Blog draft completed!');
        setLiveOutput(prev => prev + '## Final Draft\nYour blog post is ready for review!\n\n');
      }, 7000);
      
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'beginner-critic': { status: 'working', progress: 25, currentStep: 'Reading content...', output: '' }
        }));
        addChatMessage('agent', '🔍 Beginner Critic: Reviewing for accessibility...');
      }, 8000);
      
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'beginner-critic': { status: 'working', progress: 75, currentStep: 'Providing feedback...', output: '' }
        }));
        addChatMessage('agent', '💡 Beginner Critic: Providing beginner-friendly feedback...');
      }, 10000);
      
      setTimeout(() => {
        setAgents(prev => ({
          ...prev,
          'beginner-critic': { status: 'completed', progress: 100, currentStep: 'Review completed', output: '' }
        }));
        addChatMessage('agent', '✅ Beginner Critic: Review completed!');
        setIsWorkflowRunning(false);
        addChatMessage('system', '🎉 Demo workflow completed! This shows how the real-time interface works.');
      }, 12000);
    }
  };

  // Send ping to keep connection alive
  const sendPing = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  };

  // Initialize connection on mount
  useEffect(() => {
    // Try to connect to WebSocket, but don't fail if backend is not available
    try {
      connectWebSocket();
    } catch (error) {
      console.log('WebSocket connection failed, running in demo mode');
      setConnectionStatus('demo');
      addChatMessage('system', '🎭 Running in demo mode - backend not available');
    }
    
    // Set up ping interval only if connected
    const pingInterval = setInterval(() => {
      if (isConnected && wsRef.current?.readyState === WebSocket.OPEN) {
        sendPing();
      }
    }, 30000);
    
    return () => {
      clearInterval(pingInterval);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket, isConnected]);

  return (
    <div className="realtime-workspace">
      {/* Header */}
      <div className="workspace-header">
        <h1>🤖 Multi-Agent Blog Writer</h1>
        <div className="connection-status">
          <span className={`status-indicator ${connectionStatus}`}></span>
          <span>{connectionStatus}</span>
          {sessionId && <span className="session-id">Session: {sessionId}</span>}
        </div>
      </div>

      {/* Main Split-Screen Interface */}
      <div className="split-screen">
        {/* Left Panel: Chat & Control */}
        <div className="left-panel">
          <div className="control-section">
            <h3>🔑 API Key</h3>
            <form onSubmit={(e) => e.preventDefault()}>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => {
                  setApiKey(e.target.value);
                  localStorage.setItem('anthropic_api_key', e.target.value);
                }}
                placeholder="Enter your Anthropic API key..."
                className="api-key-input"
                disabled={isWorkflowRunning}
              />
            </form>
            
            <h3>🎯 Blog Concept</h3>
            <textarea
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              placeholder="Enter your blog concept here..."
              rows={3}
              disabled={isWorkflowRunning}
            />
            <button 
              onClick={startWorkflow}
              disabled={isWorkflowRunning || !apiKey.trim() || !concept.trim()}
              className="start-button"
            >
              {isWorkflowRunning ? '⏳ Creating...' : connectionStatus === 'demo' ? '🎭 Start Demo' : '🚀 Start Blog Creation'}
            </button>
            
            {/* Status indicator */}
            <div className="button-status">
              {!apiKey.trim() && <span className="status-text">⚠️ Please enter your API key</span>}
              {apiKey.trim() && !concept.trim() && <span className="status-text">⚠️ Please enter a blog concept</span>}
              {apiKey.trim() && concept.trim() && !isWorkflowRunning && connectionStatus === 'demo' && <span className="status-text">🎭 Ready for demo mode!</span>}
              {apiKey.trim() && concept.trim() && !isWorkflowRunning && connectionStatus !== 'demo' && <span className="status-text">✅ Ready to start!</span>}
            </div>
          </div>

          {/* Agent Status Grid */}
          <div className="agent-status-grid">
            <h3>🤖 Agent Status</h3>
            {Object.entries(agents).map(([agentType, agent]) => (
              <div key={agentType} className={`agent-card ${agent.status}`}>
                <div className="agent-header">
                  <span className="agent-name">{agentType.replace('-', ' ')}</span>
                  <span className="agent-status">{agent.status}</span>
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${agent.progress}%` }}
                  ></div>
                </div>
                <div className="current-step">{agent.currentStep}</div>
              </div>
            ))}
          </div>

          {/* Chat Messages */}
          <div className="chat-section">
            <h3>💬 Activity Log</h3>
            <div className="chat-messages">
              {chatMessages.map(message => (
                <div key={message.id} className={`message ${message.type}`}>
                  <span className="timestamp">{message.timestamp}</span>
                  <span className="content">{message.content}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel: Live Workspace */}
        <div className="right-panel">
          <div className="workspace-header">
            <h3>📝 Live Agent Output</h3>
            <div className="agent-tabs">
              {Object.keys(agents).map(agentType => (
                <button
                  key={agentType}
                  className={`tab ${activeTab === agentType ? 'active' : ''}`}
                  onClick={() => setActiveTab(agentType)}
                >
                  {agentType.replace('-', ' ')}
                  {agents[agentType].status === 'working' && <span className="working-indicator">●</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="live-output" ref={outputRef}>
            {liveOutput ? (
              <pre className="output-content">{liveOutput}</pre>
            ) : (
              <div className="output-placeholder">
                <div className="placeholder-content">
                  <h4>🔮 Real-time Agent Output</h4>
                  <p>Start a blog creation workflow to see agents working in real-time!</p>
                  <ul>
                    <li>💭 Watch agents think and plan</li>
                    <li>✍️ See content being written live</li>
                    <li>🔄 Observe the review process</li>
                    <li>🎯 Follow the synthesis workflow</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtimeAgentWorkspace;